import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LEGACY_THEME } from "flags";
import { useState, useMemo, useEffect } from "react";
import * as staticDataService from "services/staticDataService";

const cactus = {
  accent: {
    100: "#ebfdf1",
    200: "#ebfdf1",
    300: "#c3e8d0",
    400: "#9ad3af",
    500: "#30ac65",
    600: "#30ac65",
    700: "#218058",
    800: "#218058",
  },
  chart: {
    primary: "#9ad3af",
    secondary: "#30ac65",
    reference: "#62bee5",
  },
};

const marmalade = {
  accent: {
    100: "#feefe6",
    200: "#fff1e9",
    300: "#fdceb4",
    400: "#fcae83",
    500: "#fa7b35",
    600: "#dc7f4c",
    700: "#a45a31",
    800: "#ff5c00",
  },
  chart: {
    primary: "#ffab53",
    secondary: "#ff634e",
    reference: "#62bee5",
  },
};

const brand = {
  accent: {
    100: "#eeeeee",
    200: "#eeeeee",
    300: "#eeeeee",
    400: "#eeeeee",
    500: "#eeeeee",
    600: "#dedede",
    700: "#dedede",
    800: "#dedede",
  },
  gray: {
    100: "#fcfdfd",
    400: "#f5f7f9",
    500: "#edf0f3",
    600: "#dfe5ea",
    700: "#dce2eb",
    800: "#dbdce5",
    900: "#bdbdbd",
    label: "#a6b0c3",
    muted: "#a0a8ad",
    nav: "#9297a2",
    body: "#65676f",
    text: "#45494e",
    badgeText: "#363839",
    gainsboro: "#dcdcdc",
    secondaryMain: "#485159",
    secondaryHover: "#696D72",
    border: "#cccccc",
    hover: "#e1e4e7",
  },
  white: "#ffffff",
  black: "#000000",
  green: {
    100: "#b7f4ba",
    200: "#00cc00",
    300: "#00aa00",
    900: "#006400",
  },
  red: {
    100: "#f5beb7",
    200: "#fff0f0",
    500: "#e85f6c",
    600: "#ff0000",
    700: "#dd0000",
    800: "#710808",
    900: "#ae2918",
  },
  yellow: {
    100: "#f4f2b7",
    200: "#fffee8",
  },
  chart: {
    primary: "#eeeeee",
    secondary: "#dedede",
    reference: "#62bee5",
  },
  mapper: {
    mapped: "#effff7",
    unmapped: "#f3f4f7",
    missing: "#ffefee",
  },
  compare: {
    new: "#ccffcc",
    missing: "#fcdfcf",
    different: "#ffcccc",
  },
  status: {
    closed: "#ededec",
    opportunity: "gold",
    inProgress: "dodgerblue",
    bound: "mediumseagreen",
    notTakenUp: "mediumslateblue",
  },
  review: {
    unset: "gainsboro",
    reviewed: "green",
    pending: "gold",
    unknown: "grey",
  },
  severity: {
    low: "deepskyblue",
    lowContrast: "white",
    medium: "gold",
    mediumContrast: "#845e00",
    high: "orange",
    highContrast: "#6f3c07",
    maximum: "red",
  },
  priority: {
    high: "#ffebee",
    medium: "#fff3e0",
    low: "#e8f5e9",
    default: "#f5f5f5",
  },
};

export const initialThemeOptions = {
  cssVariables: true,
  brand,
  typography: {
    fontFamily: [
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
  palette: {
    primary: {
      main: brand.accent[500],
      hover: brand.accent[700],
      dark: brand.accent[700],
      light: brand.accent[400],
      lighter: brand.accent[300],
      lightest: brand.accent[100],
    },
    secondary: {
      main: brand.gray.secondaryMain,
      hover: brand.gray.secondaryHover,
      lightest: brand.gray[500],
    },
  },
  components: {
    MuiInputBase: {
      defaultProps: {
        disableInjectingGlobalStyles: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: "bold",
          // Only the contained variants sit on a filled accent, so only they
          // want white text; outlined, text and inherit buttons keep the
          // palette's own colour. Written as `variants` rather than the
          // `containedPrimary` / `containedSecondary` slots, which MUI v6
          // deprecates and v7 removes. The array must sit inside the slot:
          // a `variants` key alongside `root` is silently ignored in v6.
          variants: [
            {
              props: { variant: "contained", color: "primary" },
              style: { color: brand.white },
            },
            {
              props: { variant: "contained", color: "secondary" },
              style: { color: brand.white },
            },
          ],
        },
      },
    },
  },
};

function Theme({ children }) {
  const [themeOptions, setThemeOptions] = useState(initialThemeOptions);
  const {
    data: featureFlags,
    isSuccess: flagsLoaded,
  } = staticDataService.useFeatureFlagsQuery();
  const useLegacyTheme = flagsLoaded
    ? (featureFlags?.[LEGACY_THEME] ?? true) === true
    : null;

  useEffect(() => {
    if (useLegacyTheme == null) {
      return;
    }
    const variant = useLegacyTheme ? marmalade : cactus;
    const { accent, chart } = variant;
    setThemeOptions((prev) => ({
      ...prev,
      brand: {
        ...prev.brand,
        accent,
        chart,
      },
      palette: {
        ...prev.palette,
        primary: {
          main: accent[500],
          hover: accent[700],
          dark: accent[700],
          light: accent[400],
          lighter: accent[300],
          lightest: accent[100],
        },
      },
    }));
  }, [useLegacyTheme]);

  const theme = useMemo(() => createTheme(themeOptions), [themeOptions]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export default Theme;
